import { useEffect, useState, useCallback, memo } from "react";
import FileViewer from "react-file-viewer";

// Supported MIME types mapping
const mimeTypes = {
  bmp: "image/bmp",
  csv: "text/csv",
  odt: "application/vnd.oasis.opendocument.text",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  gif: "image/gif",
  htm: "text/htm",
  html: "text/html",
  jpg: "image/jpg",
  jpeg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  tiff: "image/tiff",
  txt: "text/plain",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  mp4: "video/mp4",
  webp: "image/webp",
};

const DocumentRenderer = memo(
  ({ previewUrl, fileType, width = "50%", height = "50vh" }) => {
    const [error, setError] = useState(null);

    if (!previewUrl) {
      return <div>No document to preview</div>;
    }

    const onError = (e) => {
      console.error("FileViewer error:", e);
      setError("Error loading document");
    };

    if (error) {
      return <div style={{ padding: 10 }}>{error}</div>;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: height,
          width: "100%",
          border: "1px solid #ccc",
        }}
      >
        <div style={{ width, height }}>
          <FileViewer
            fileType={fileType}
            filePath={previewUrl}
            onError={onError}
          />
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.previewUrl === nextProps.previewUrl &&
      prevProps.fileType === nextProps.fileType &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height
    );
  }
);

const DocumentPreviewUI = memo(
  ({ documentUrl, previewURL, width = "50%", height = "50vh" }) => {
    const [documentType, setDocumentType] = useState(null);

    const extractDocumentType = useCallback(() => {
      if (documentUrl) {
        const type = documentUrl.split(".").pop()?.toLowerCase();
        setDocumentType(type || null);
      } else {
        setDocumentType(null);
      }
    }, [documentUrl]);

    useEffect(() => {
      extractDocumentType();
    }, [extractDocumentType]);

    if (!documentUrl || !previewURL) return null;

    if (!documentType || !mimeTypes[documentType]) {
      return (
        <div className="p-5">
          Unsupported file type: {documentType || "unknown"} - You will need to download to preview.
        </div>
      );
    }

    return (
      <DocumentRenderer
        previewUrl={previewURL}
        fileType={documentType}
        width={width}
        height={height}
      />
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.documentUrl === nextProps.documentUrl &&
      prevProps.previewURL === nextProps.previewURL &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height
    );
  }
);

export { DocumentPreviewUI };
